/**
 * shared/src/schemas/route.schema.ts
 *
 * Shared DTO schemas for Routes.
 * Used by BOTH client (API calls) and server (validation).
 *
 * FASE 5: Contract testing — se il server cambia la shape,
 * il client non compila più. Zero regressioni silenziose.
 */
import { z } from "zod";
// ─── Waypoint ────────────────────────────────────────────────────────────────
export const GpsWaypointSchema = z
    .object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    ts: z.number().optional(),
    // Nullable: il client (GPS fallback/interpolazione) può inviare null
    accuracy_m: z.number().min(0).max(500).nullable().optional(),
    heading: z.number().min(0).max(360).nullable().optional(),
    speed_mps: z.number().min(0).max(111).nullable().optional(), // 400 km/h max
    // Flag punti interpolati (gap filling) — accettato e preservato al confine.
    interpolated: z.boolean().optional(),
})
    .passthrough(); // Tollerante: field extra dei client futuri non rompono il contratto
// ─── Route ───────────────────────────────────────────────────────────────────
export const RouteSchema = z
    .object({
    id: z.string().uuid(),
    user_id: z.string().uuid(),
    name: z.string().min(2).max(200),
    description: z.string().max(2000).nullable().optional(),
    distance_km: z.number().nonnegative().nullable().optional(),
    duration_min: z.number().nonnegative().nullable().optional(),
    waypoints: z.array(GpsWaypointSchema).optional().default([]),
    bike_id: z.string().uuid().nullable().optional(),
    visibility: z
        .enum(["public", "private", "shared"])
        .optional()
        .default("public"),
    source: z
        .enum(["recorded", "planned", "gpx"])
        .optional()
        .default("recorded"),
    difficulty_level: z
        .enum(["easy", "medium", "hard"])
        .optional()
        .default("easy"),
    created_at: z.string().datetime().optional(),
    updated_at: z.string().datetime().optional(),
})
    .passthrough(); // Allow extra fields from DB joins
// ─── Create Route Input ──────────────────────────────────────────────────────
export const CreateRouteInputSchema = z.object({
    name: z.string().min(2).max(200),
    description: z.string().max(2000).optional().default(""),
    waypoints: z.array(GpsWaypointSchema).optional().default([]),
    geometry: z.any().optional().nullable(),
    meeting_point: z.any().optional().nullable(),
    source: z.enum(["recorded", "planned", "gpx"]).optional().default("recorded"),
    distance_km: z.number().nonnegative().optional().default(0),
    duration_min: z.number().nonnegative().optional().nullable(),
    difficulty_level: z
        .enum(["easy", "medium", "hard"])
        .optional()
        .default("easy"),
    is_public: z.boolean().optional().default(true),
});
// ─── Route Weather (FEAT METEO-PERCORSO 18 Set 2026) ───────────────────────
// Meteo rilevato durante il giro, salvato sul percorso registrato.
export const RouteWeatherSchema = z
    .object({
    temp: z.number().optional(),
    apparent_temp: z.number().optional(),
    humidity: z.number().optional(),
    wind_speed: z.number().optional(),
    weather_code: z.number().optional(),
    label: z.string().optional(),
    emoji: z.string().optional(),
    precipitation_probability: z.number().optional(),
    recorded_at: z.string().optional(),
})
    .passthrough();
// ─── Chunked Route Upload (POST /api/routes/chunked) ────────────────────────
//
// FIX CRITICO (Agosto 2026) — "Expected array, received string":
// Il client comprime i waypoint con pako.deflate → base64 e li invia come
// STRINGA con `compressed: true`. Il vecchio schema dichiarava
// `waypoints: z.array(...)` e Zod rifiutava il payload PRIMA che il server
// potesse decomprimere. Questo schema UNION è la Single Source of Truth
// usata sia dal client (pre-flight) sia dal server (boundary validation).
export const ChunkedRouteInputSchema = z
    .object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional().default(""),
    difficulty_level: z
        .enum(["easy", "medium", "hard"])
        .optional()
        .default("easy"),
    is_public: z.boolean().optional().default(true),
    distance_km: z.number().nonnegative().optional().default(0),
    duration_min: z.number().nonnegative().optional().default(0),
    // UNION: array di waypoint (legacy) OPPURE stringa base64 (compresso).
    waypoints: z.union([z.array(GpsWaypointSchema), z.string().min(1)]),
    compressed: z.boolean().optional().default(false),
    chunk_index: z.number().int().nonnegative().optional(),
    // FEAT METEO-PERCORSO: meteo durante il giro (opzionale)
    weather: RouteWeatherSchema.nullable().optional(),
})
    .superRefine((data, ctx) => {
    // Tolleranza: compressed=true con ARRAY è accettato (client legacy
    // che invia array già decompressi con il flag attivo).
    // Rigore: compressed=false/omesso con STRINGA è un errore di contratto.
    if (!data.compressed && typeof data.waypoints === "string") {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["waypoints"],
            message: "waypoints è una stringa ma compressed=false: imposta compressed=true o invia un array di waypoint.",
        });
    }
});
/** Response di POST /api/routes/chunked — usata nei contract test E2E. */
export const ChunkedRouteUploadResponseSchema = z.object({
    route: z.object({ id: z.string() }),
    points_inserted: z.number().int().nonnegative(),
    validated_km: z.number().nonnegative(),
});
// ─── Route Share ─────────────────────────────────────────────────────────────
export const RouteShareSchema = z.object({
    id: z.string().uuid(),
    route_id: z.string().uuid(),
    from_user_id: z.string().uuid(),
    to_user_id: z.string().uuid(),
    status: z.enum(["pending", "accepted", "rejected"]),
    created_at: z.string().datetime().optional(),
    decided_at: z.string().datetime().nullable().optional(),
});
// ─── API Response Wrappers ───────────────────────────────────────────────────
export const RoutesListResponseSchema = z.object({
    routes: z.array(RouteSchema),
});
export const RouteResponseSchema = z.object({
    route: RouteSchema,
});
export const RouteShareResponseSchema = z.object({
    share: RouteShareSchema,
});
//# sourceMappingURL=route.schema.js.map